import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  Stack,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Close,
  Info,
} from '@mui/icons-material';
import { useWebVitals, formatMetricValue, getMetricDescription } from '@/hooks/useWebVitals';
import type { MetricName, MetricRating } from '@/services/webVitals';

// Local storage key for overlay preferences
const OVERLAY_PREFS_KEY = 'webVitalsOverlay';

interface OverlayPreferences {
  isMinimized: boolean;
  isVisible: boolean;
  position: { x: number; y: number };
}

const defaultPrefs: OverlayPreferences = {
  isMinimized: false,
  isVisible: true,
  position: { x: 20, y: 20 },
};

export function WebVitalsOverlay() {
  const { metrics, overallRating, isCollecting } = useWebVitals();
  const [prefs, setPrefs] = useState<OverlayPreferences>(() => {
    const stored = localStorage.getItem(OVERLAY_PREFS_KEY);
    return stored ? JSON.parse(stored) : defaultPrefs;
  });

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem(OVERLAY_PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  if (!prefs.isVisible) {
    return null;
  }

  const toggleMinimized = () => {
    setPrefs(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
  };

  const handleClose = () => {
    setPrefs(prev => ({ ...prev, isVisible: false }));
  };

  const getRatingColor = (rating: MetricRating): string => {
    switch (rating) {
      case 'good':
        return '#4caf50';
      case 'needs-improvement':
        return '#ff9800';
      case 'poor':
        return '#f44336';
      default:
        return '#757575';
    }
  };

  const getRatingBgColor = (rating: MetricRating): string => {
    switch (rating) {
      case 'good':
        return 'rgba(76, 175, 80, 0.1)';
      case 'needs-improvement':
        return 'rgba(255, 152, 0, 0.1)';
      case 'poor':
        return 'rgba(244, 67, 54, 0.1)';
      default:
        return 'rgba(117, 117, 117, 0.1)';
    }
  };

  const metricOrder: MetricName[] = ['LCP', 'FCP', 'CLS', 'INP', 'TTFB'];

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: prefs.position.y,
        right: prefs.position.x,
        width: prefs.isMinimized ? 200 : 320,
        zIndex: 9999,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: getRatingBgColor(overallRating.rating),
          borderBottom: 1,
          borderColor: 'divider',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" fontWeight="bold">
            Web Vitals
          </Typography>
          {isCollecting && (
            <Box sx={{ width: 16, height: 16 }}>
              <LinearProgress
                sx={{
                  height: 2,
                  borderRadius: 1,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getRatingColor(overallRating.rating),
                  },
                }}
              />
            </Box>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={toggleMinimized}
            sx={{ p: 0.5 }}
          >
            {prefs.isMinimized ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ p: 0.5 }}
          >
            <Close />
          </IconButton>
        </Stack>
      </Box>

      {/* Minimized View */}
      {prefs.isMinimized && (
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Score: ${overallRating.score}`}
              size="small"
              sx={{
                backgroundColor: getRatingBgColor(overallRating.rating),
                color: getRatingColor(overallRating.rating),
                fontWeight: 'bold',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {Object.keys(metrics).length} metrics
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Expanded View */}
      <Collapse in={!prefs.isMinimized}>
        <Box sx={{ p: 2 }}>
          {/* Overall Score */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Overall Score
                </Typography>
                <Typography variant="h5" fontWeight="bold" color={getRatingColor(overallRating.rating)}>
                  {overallRating.score}
                </Typography>
              </Box>
              <Stack spacing={0.5}>
                <Chip
                  label={`${overallRating.details.good} Good`}
                  size="small"
                  sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
                />
                <Chip
                  label={`${overallRating.details.needsImprovement} Needs Work`}
                  size="small"
                  sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800' }}
                />
                <Chip
                  label={`${overallRating.details.poor} Poor`}
                  size="small"
                  sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#f44336' }}
                />
              </Stack>
            </Stack>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Individual Metrics */}
          <Stack spacing={1.5}>
            {metricOrder.map((metricName) => {
              const metric = metrics[metricName];
              
              if (!metric) {
                return (
                  <Box key={metricName} sx={{ opacity: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {metricName}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Collecting...
                      </Typography>
                    </Stack>
                  </Box>
                );
              }

              return (
                <Box
                  key={metricName}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: getRatingBgColor(metric.rating),
                    border: 1,
                    borderColor: getRatingColor(metric.rating),
                    borderStyle: 'solid',
                    borderWidth: '1px',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {metric.name}
                      </Typography>
                      <Tooltip title={getMetricDescription(metric.name)}>
                        <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Tooltip>
                    </Stack>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={getRatingColor(metric.rating)}
                    >
                      {formatMetricValue(metric.name, metric.value)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          {/* Footer */}
          <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              Development mode only • Drag to reposition
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// Helper component to show/hide overlay
export function WebVitalsOverlayToggle() {
  const [isVisible, setIsVisible] = useState(() => {
    const stored = localStorage.getItem(OVERLAY_PREFS_KEY);
    if (stored) {
      const prefs = JSON.parse(stored);
      return prefs.isVisible;
    }
    return true;
  });

  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    
    const stored = localStorage.getItem(OVERLAY_PREFS_KEY);
    const prefs = stored ? JSON.parse(stored) : defaultPrefs;
    prefs.isVisible = newVisibility;
    localStorage.setItem(OVERLAY_PREFS_KEY, JSON.stringify(prefs));
    
    // Force re-render of overlay
    window.dispatchEvent(new Event('storage'));
  };

  return (
    <Tooltip title={isVisible ? 'Hide Web Vitals' : 'Show Web Vitals'}>
      <IconButton onClick={toggleVisibility} size="small">
        <Info />
      </IconButton>
    </Tooltip>
  );
}